import { useState, useEffect, useCallback, useRef } from 'react';
import { WorkItem } from '../components/workitems/workItemTypes';
import { WorkItemChangePayload } from '@techgitter/contracts';
import { getAllWorkItems, getWorkItemById } from '../services/workItemDataService';
import { useSignalREvent } from './useSignalR';

/**
 * Custom hook for managing work item data from the API.
 * Returns { workItems, isLoading, error } following project conventions.
 */
export function useWorkItemData() {
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadWorkItems = useCallback(async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[WorkItemData] loadWorkItems called — fetching from API...');
      const data = await getAllWorkItems(signal);
      console.log('[WorkItemData] loadWorkItems complete —', data.length, 'items. displayIndex sample:', data.slice(0, 3).map(w => `${w.title}:${w.displayIndex}`));
      setWorkItems(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Failed to load work items';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadWorkItems(controller.signal);
    return () => controller.abort('useEffect cleanup');
  }, [loadWorkItems]);

  const refresh = useCallback(() => {
    loadWorkItems();
  }, [loadWorkItems]);

  // Track GUIDs of items recently changed via SignalR (for UI highlight effect).
  const [changedGuids, setChangedGuids] = useState<Set<string>>(new Set());
  const changedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const markChanged = useCallback((...guids: string[]) => {
    setChangedGuids(prev => {
      const next = new Set(prev);
      guids.forEach(g => next.add(g));
      return next;
    });
    for (const guid of guids) {
      const existing = changedTimersRef.current.get(guid);
      if (existing) clearTimeout(existing);
      changedTimersRef.current.set(guid, setTimeout(() => {
        setChangedGuids(prev => {
          const next = new Set(prev);
          next.delete(guid);
          return next;
        });
        changedTimersRef.current.delete(guid);
      }, 2100));
    }
  }, []);

  // Temporarily suppress SignalR-driven state updates after a reorder so that
  // cascading Dataverse webhooks (which may carry stale displayIndex values)
  // don't overwrite the optimistic order before propagation completes.
  const reorderLockUntilRef = useRef<number>(0);

  const lockForReorder = useCallback((durationMs = 5000) => {
    console.log('[WorkItemData] lockForReorder — suppressing SignalR events for', durationMs, 'ms');
    reorderLockUntilRef.current = Date.now() + durationMs;
  }, []);

  // Patch local state when SignalR broadcasts a workitems:changed event.
  // Falls back to full refresh for missing/malformed payloads (backward compatible).
  const handleWorkItemChange = useCallback(
    (data: unknown) => {
      const payload = data as WorkItemChangePayload | undefined;
      const lockRemaining = reorderLockUntilRef.current - Date.now();

      console.log('[WorkItemData] SignalR workitems:changed received', {
        rawData: data,
        action: payload?.action ?? '(none)',
        hasWorkItem: !!(payload as Record<string, unknown>)?.workItem,
        reorderLockActive: lockRemaining > 0,
        lockRemainingMs: lockRemaining > 0 ? lockRemaining : 0,
      });

      // While a reorder lock is active, suppress all events to protect optimistic state.
      if (lockRemaining > 0) {
        console.log('[WorkItemData] SUPPRESSED by reorder lock — ignoring event');
        return;
      }

      if (!payload || !payload.action) {
        console.log('[WorkItemData] No action in payload — triggering full refresh');
        refresh();
        return;
      }

      // Reorder events: the originating client is protected by the reorder
      // lock above, so this path only runs on *other* clients — patch
      // displayIndex values in-place.
      if (payload.action === 'reordered') {
        const reorderedItems = (payload as { items?: { guid: string; displayIndex: number }[] }).items;
        if (reorderedItems?.length) {
          console.log('[WorkItemData] Reorder event — patching', reorderedItems.length, 'items in-place');
          const indexByGuid = new Map(reorderedItems.map(i => [i.guid, i.displayIndex]));
          markChanged(...reorderedItems.map(i => i.guid));
          setWorkItems(prev => prev.map(w => {
            const newIndex = indexByGuid.get(w.guid);
            return newIndex !== undefined ? { ...w, displayIndex: newIndex } : w;
          }));
        } else {
          console.log('[WorkItemData] Reorder event without items — full refresh');
          refresh();
        }
        return;
      }

      console.log('[WorkItemData] Processing action:', payload.action);

      switch (payload.action) {
        case 'updated':
          if (payload.workItem?.id) {
            console.log('[WorkItemData] Patching updated item:', payload.workItem.id, 'displayIndex:', (payload.workItem as unknown as Record<string, unknown>).displayIndex);
            markChanged(payload.workItem.guid);
            setWorkItems(prev => {
              if (!prev.some(w => w.id === payload.workItem.id)) {
                // Item not in local state (e.g. newly assigned to us) — full refresh
                console.log('[WorkItemData] Item not in local state — triggering full refresh');
                refresh();
                return prev;
              }
              return prev.map(w =>
                w.id === payload.workItem.id ? { ...w, ...payload.workItem } : w
              );
            });
          } else {
            console.log('[WorkItemData] Updated event but no workItem.id — triggering full refresh');
            refresh();
          }
          break;

        case 'created':
          if (payload.workItem?.id) {
            console.log('[WorkItemData] Created item:', payload.workItem.id);
            markChanged(payload.workItem.guid);
            setWorkItems(prev => {
              if (prev.some(w => w.id === payload.workItem.id)) {
                return prev.map(w =>
                  w.id === payload.workItem.id ? { ...w, ...payload.workItem } : w
                );
              }
              return [...prev, payload.workItem];
            });
          } else {
            refresh();
          }
          break;

        case 'deleted': {
          const deletedId = 'workItemId' in payload ? payload.workItemId : undefined;
          const deletedGuid = 'workItemGuid' in payload ? (payload as { workItemGuid: string }).workItemGuid : undefined;
          if (deletedId || deletedGuid) {
            console.log('[WorkItemData] Deleted item:', deletedId ?? deletedGuid);
            setWorkItems(prev => prev.filter(w =>
              (deletedId ? w.id !== deletedId : true) &&
              (deletedGuid ? w.guid !== deletedGuid : true)
            ));
          } else {
            refresh();
          }
          break;
        }

        default:
          console.log('[WorkItemData] Unknown action:', (payload as { action?: string }).action, '— triggering full refresh');
          refresh();
          break;
      }
    },
    [refresh, markChanged],
  );

  useSignalREvent('workitems:changed', handleWorkItemChange);

  // Check-In sessions are projected onto the work-item dashboard as a new
  // WorkItemType. Lifecycle/payment changes from the orchestrator broadcast
  // `check_in_session:changed` — we treat them as a coarse signal to refresh
  // the affected row, since the existing workitems:changed pipeline doesn't
  // know about session-level state.
  useSignalREvent('check_in_session:changed', () => {
    refresh();
  });

  // Client-side filter helpers (operate on already-fetched work items)
  const getWorkItemsByTechnician = useCallback(
    (name: string) => workItems.filter(w => w.userFullName === name),
    [workItems]
  );

  const getWorkItemsByStatus = useCallback(
    (status: string) => workItems.filter(w => w.status === status),
    [workItems]
  );

  const getUrgentWorkItems = useCallback(
    () => workItems.filter(w => w.type === 'job' && w.jobPriority === '1'),
    [workItems]
  );

  const getOpenWorkItems = useCallback(
    () => getWorkItemsByStatus('open'),
    [getWorkItemsByStatus]
  );

  const updateWorkItem = useCallback(
    (id: string, updates: Partial<WorkItem>) => {
      if ('displayIndex' in updates) {
        console.log('[WorkItemData] updateWorkItem (optimistic):', id, 'displayIndex →', updates.displayIndex);
      }
      setWorkItems(prev => prev.map(w => (w.id === id || w.guid === id) ? { ...w, ...updates } : w));
    },
    [],
  );

  const removeWorkItem = useCallback(
    (id: string): WorkItem | undefined => {
      let removed: WorkItem | undefined;
      setWorkItems(prev => {
        removed = prev.find(w => w.id === id || w.guid === id);
        return prev.filter(w => w.id !== id && w.guid !== id);
      });
      return removed;
    },
    [],
  );

  const restoreWorkItem = useCallback(
    (item: WorkItem) => {
      setWorkItems(prev => {
        if (prev.some(w => w.id === item.id)) return prev;
        return [...prev, item];
      });
    },
    [],
  );

  const searchWorkItemsLocal = useCallback(
    (term: string) => {
      const lower = term.toLowerCase();
      return workItems.filter(
        w =>
          w.title.toLowerCase().includes(lower) ||
          w.customerName.toLowerCase().includes(lower)
      );
    },
    [workItems]
  );

  return {
    workItems,
    isLoading,
    error,
    refresh,
    updateWorkItem,
    removeWorkItem,
    restoreWorkItem,
    lockForReorder,
    changedGuids,
    markChanged,
    getWorkItemsByTechnician,
    getWorkItemsByStatus,
    getUrgentWorkItems,
    getOpenWorkItems,
    searchWorkItems: searchWorkItemsLocal,
  };
}

/**
 * Hook for getting a specific work item by ID
 */
export function useWorkItem(workItemId: string) {
  const [workItem, setWorkItem] = useState<WorkItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // Direct by-id lookup — needed so resolved jobs (e.g. the
        // original ticket on an in_store_return) are findable.
        const item = await getWorkItemById(workItemId, controller.signal);
        setWorkItem(item);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load work item');
      } finally {
        setIsLoading(false);
      }
    };

    if (workItemId) {
      load();
    }

    return () => controller.abort('useEffect cleanup');
  }, [workItemId]);

  return { workItem, isLoading, error };
}

/**
 * Hook for getting work items by technician
 */
export function useWorkItemsByTechnician(technicianName: string) {
  const { workItems, isLoading, error } = useWorkItemData();
  const filtered = workItems.filter(w => w.userFullName === technicianName);
  return { workItems: filtered, isLoading, error, count: filtered.length };
}
